(() => {
  const instances = new WeakMap();
  const selectors = {
    root: '[data-category-collection-section]',
    grid: '[data-category-collection-grid]',
    card: '[data-category-collection-card]',
    sentinel: '[data-category-collection-sentinel]',
    status: '[data-category-collection-status]',
    retry: '[data-category-collection-retry]'
  };

  class CategoryCollection1 {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.categoryCollectionSection;
      this.grid = root.querySelector(selectors.grid);
      this.sentinel = root.querySelector(selectors.sentinel);
      this.status = root.querySelector(selectors.status);
      this.retry = root.querySelector(selectors.retry);
      this.nextUrl = this.sentinel ? this.sentinel.dataset.nextUrl : '';
      this.loadedUrls = new Set();
      this.isLoading = false;
      this.observer = null;
      this.restoreQueued = false;

      this.onIntersection = this.onIntersection.bind(this);
      this.onRetryClick = this.onRetryClick.bind(this);
      this.onPageHide = this.onPageHide.bind(this);
      this.onProductClick = this.onProductClick.bind(this);
    }

    init() {
      if (!this.grid || !this.sentinel || !this.sectionId || !('IntersectionObserver' in window)) {
        return;
      }

      this.root.classList.add('category-collection-1-js-ready');
      this.restoreState();
      this.retry?.addEventListener('click', this.onRetryClick);
      this.grid.addEventListener('click', this.onProductClick);
      window.addEventListener('pagehide', this.onPageHide);

      if (this.nextUrl) {
        this.createObserver();
      } else {
        this.setStatus(this.grid.children.length ? 'All products loaded.' : '');
      }
    }

    destroy() {
      this.disconnectObserver();
      this.retry?.removeEventListener('click', this.onRetryClick);
      this.grid?.removeEventListener('click', this.onProductClick);
      window.removeEventListener('pagehide', this.onPageHide);
      this.root.classList.remove('category-collection-1-js-ready');
    }

    createObserver() {
      this.disconnectObserver();
      this.observer = new IntersectionObserver(this.onIntersection, {
        rootMargin: '600px 0px 600px 0px'
      });
      this.observer.observe(this.sentinel);
    }

    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    onIntersection(entries) {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.loadNextPage();
      }
    }

    onRetryClick() {
      this.retry.hidden = true;
      this.loadNextPage();
    }

    onProductClick(event) {
      const link = event.target.closest('a[href]');

      if (link && this.grid.contains(link)) {
        this.saveState();
      }
    }

    onPageHide() {
      this.saveState();
    }

    async loadNextPage() {
      if (this.isLoading || !this.nextUrl || this.loadedUrls.has(this.nextUrl)) {
        return;
      }

      const requestedUrl = this.nextUrl;
      this.isLoading = true;
      this.loadedUrls.add(requestedUrl);
      this.disconnectObserver();
      this.setStatus('Loading more products...');

      try {
        const html = await this.fetchSection(requestedUrl);
        const parsed = this.parseSection(html);

        if (!parsed.cards.length) {
          this.nextUrl = '';
          this.setStatus('All products loaded.');
          this.clearStaleState();
          return;
        }

        parsed.cards.forEach((card) => this.grid.appendChild(card));
        this.nextUrl = parsed.nextUrl;

        if (this.sentinel) {
          this.sentinel.dataset.nextUrl = this.nextUrl;
        }

        this.setStatus(this.nextUrl ? '' : 'All products loaded.');
        this.saveState();

        if (this.nextUrl) {
          this.createObserver();
        }
      } catch (error) {
        this.loadedUrls.delete(requestedUrl);
        this.setStatus('Products could not be loaded. Please try again.');

        if (this.retry) {
          this.retry.hidden = false;
        }
      } finally {
        this.isLoading = false;
      }
    }

    async fetchSection(url) {
      const requestUrl = new URL(url, window.location.origin);
      requestUrl.searchParams.set('section_id', this.sectionId);

      const response = await fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`Section request failed: ${response.status}`);
      }

      return response.text();
    }

    parseSection(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const returnedRoot = doc.querySelector(`${selectors.root}[data-category-collection-section="${this.sectionId}"]`) || doc;
      const cards = Array.from(returnedRoot.querySelectorAll(selectors.card));
      const returnedSentinel = returnedRoot.querySelector(selectors.sentinel);
      const nextUrl = returnedSentinel ? returnedSentinel.dataset.nextUrl : '';

      return { cards, nextUrl };
    }

    setStatus(message) {
      if (this.status) {
        this.status.textContent = message;
      }
    }

    storageKey() {
      return `category-collection-1:${window.location.origin}${window.location.pathname}`;
    }

    saveState() {
      if (!this.grid) {
        return;
      }

      const state = {
        cardHtml: this.grid.innerHTML,
        nextUrl: this.nextUrl,
        loadedUrls: Array.from(this.loadedUrls),
        scrollY: window.scrollY,
        savedAt: Date.now()
      };

      try {
        sessionStorage.setItem(this.storageKey(), JSON.stringify(state));
      } catch (error) {
        this.clearStaleState();
      }
    }

    restoreState() {
      let state;

      try {
        state = JSON.parse(sessionStorage.getItem(this.storageKey()));
      } catch (error) {
        this.clearStaleState();
        return;
      }

      if (!state || !state.cardHtml || Date.now() - state.savedAt > 30 * 60 * 1000) {
        this.clearStaleState();
        return;
      }

      const currentCount = this.grid.querySelectorAll(selectors.card).length;
      const template = document.createElement('template');
      template.innerHTML = state.cardHtml;
      const savedCount = template.content.querySelectorAll(selectors.card).length;

      if (savedCount > currentCount) {
        this.grid.innerHTML = state.cardHtml;
        this.nextUrl = state.nextUrl || '';
        this.loadedUrls = new Set(Array.isArray(state.loadedUrls) ? state.loadedUrls : []);

        if (this.sentinel) {
          this.sentinel.dataset.nextUrl = this.nextUrl;
        }

        this.restoreQueued = true;
        window.requestAnimationFrame(() => {
          window.scrollTo({ top: state.scrollY || 0, behavior: 'auto' });
          this.restoreQueued = false;
        });
      }
    }

    clearStaleState() {
      try {
        sessionStorage.removeItem(this.storageKey());
      } catch (error) {
        // Ignore unavailable storage.
      }
    }
  }

  const initRoot = (root) => {
    if (!root || instances.has(root)) {
      return;
    }

    const instance = new CategoryCollection1(root);
    instances.set(root, instance);
    instance.init();
  };

  const destroyRoot = (root) => {
    const instance = instances.get(root);

    if (instance) {
      instance.destroy();
      instances.delete(root);
    }
  };

  const initAll = (container = document) => {
    container.querySelectorAll(selectors.root).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches(selectors.root)) {
      destroyRoot(event.target);
    }

    event.target.querySelectorAll(selectors.root).forEach(destroyRoot);
  });
})();
